/**
 * Delivery staff login — username/password created by shop owner.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from '@mui/material';
import {
  clearStaffSession,
  deliveryStaffApi,
  staffAppPath,
} from '../lib/deliveryStaffApi';

export default function DeliveryStaffLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      clearStaffSession();
      await deliveryStaffApi.login({
        username: username.trim(),
        password,
      });
      navigate(staffAppPath('/delivery-staff/orders'), { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : 'Login failed — check username and password'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f3f7f6',
        display: 'flex',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="xs">
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, textAlign: 'center', mb: 0.5, color: '#0f766e' }}
        >
          Delivery Staff
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', mb: 3 }}
        >
          Sign in with the account your shop admin created.
        </Typography>
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                margin="normal"
                size="small"
                required
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                margin="normal"
                size="small"
                required
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={busy || !username.trim() || !password}
                sx={{
                  mt: 2,
                  py: 1.25,
                  bgcolor: '#0f766e',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#0d9488' },
                }}
              >
                {busy ? <CircularProgress size={22} color="inherit" /> : 'Sign in'}
              </Button>
            </Box>
          </CardContent>
        </Card>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 2 }}
        >
          Tip: Add local.gosnel.com to Home Screen, then enable Order alerts
          after login for notifications.
        </Typography>
      </Container>
    </Box>
  );
}
